import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Pagination,
  IconButton,
  Dialog,
  RadioGroup,
  FormControl,
  DialogContent,
  DialogActions,
  DialogTitle,
  Radio,
  CircularProgress,
} from "@mui/material";
import RequestCard from "./RequestCard";
import { Request, RequestListProps } from "./types";
import FilterListIcon from "@mui/icons-material/FilterList";
import { getLabelMap, defaultLabelMap } from "./manageRequestsService";

const tabs = ["All", "Collection", "Order-Taking", "Order-Bypass", "Credit-Limit"];
const statusOptions = ["All", "Pending", "Approved", "Rejected"];

const RequestList: React.FC<RequestListProps> = ({ loading, data = [], onApprove, onReject, statusFilter, onStatusFilterChange }) => {
  const [selectedTab, setSelectedTab] = useState<string>("All");
  // const [selectAll, setSelectAll] = useState<boolean>(false);
  const [selectedRequests, setSelectedRequests] = useState<Set<string>>(new Set());
  const [page, setPage] = useState<number>(1);
  const [labelMap, setLabelMap] = useState(defaultLabelMap);
  const itemsPerPage = 10;

  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement | null>(null);

  const [requestDataMap, setRequestDataMap] = useState<Record<string, any>>(() => {
    const initial: Record<string, any> = {};
    data.forEach((req) => {
      initial[req.id] = req.data;
    });
    return initial;
  });

  useEffect(() => {
    (async () => {
      try {
        const map = await getLabelMap();
        setLabelMap(map);
      } catch (err) {
        console.warn("Using default label map due to error:", err);
        setLabelMap(defaultLabelMap);
      }
    })();
  }, []);

  const tabs = useMemo(() => {
    const dynamicTabs = Object.entries(labelMap).map(([key, value]) => value.text);
    return ["All", ...dynamicTabs];
  }, [labelMap]);

  // 🔹 Filter requests based on tab
  const filteredRequests = useMemo(() => {
  // Normalize selected tab (lowercase + replace spaces with "-")
  const normalizedTab =
    selectedTab.trim().toLowerCase().replace(/\s+/g, "-");

  let list =
    normalizedTab === "all"
      ? data
      : data.filter((r) => r.type === normalizedTab);
  if(statusFilter == "Pending"){
    list = list.filter((r) => r.status?.toLowerCase() === "supervisor_approved" || r.status?.toLowerCase() === "pending");
  }else if (statusFilter !== "All") {
    list = list.filter((r) => r.status?.toLowerCase() === statusFilter.toLowerCase());
  }

  return list;
}, [data, selectedTab, statusFilter]);


  // 🔹 Pagination
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // 🔹 Select all toggle
  // const handleSelectAll = (checked: boolean) => {
  //   setSelectAll(checked);
  //   if (checked) {
  //     setSelectedRequests(new Set(filteredRequests.map((r) => r.id)));
  //   } else {
  //     setSelectedRequests(new Set());
  //   }
  // };

  // 🔹 Individual select
  const handleRequestSelect = (id: string, checked: boolean) => {
    const updated = new Set(selectedRequests);
    if (checked) updated.add(id);
    else updated.delete(id);

    setSelectedRequests(updated);
    // setSelectAll(updated.size === filteredRequests.length);
  };

  const handleDataChange = (id: string, newData: any) => {
    if (newData == "") newData = "0";
    setRequestDataMap((prev) => ({
      ...prev,
      [id]: newData,
    }));
  };

  const approveAllEnabled = selectedRequests.size > 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        filterRef.current &&
        !filterRef.current.contains(target) &&
        !(event.target as HTMLElement).closest('button')?.contains(target)
      ) {
        setFilterOpen(false);
      }
    };
    if (filterOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filterOpen]);

  useEffect(() => {
    setPage(1);
    setSelectedTab("All");
    onStatusFilterChange("All");
  }, [data]);

  return (
    <Box
      sx={{
        bgcolor: "white",
        color: "black",
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* 🔹 Top Tabs */}
      <Box
        sx={{
          overflowX: "auto", // enable horizontal scroll
          whiteSpace: "nowrap", // keep items in one line
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "white",
          px: 1,
          py: 1.5,
          scrollbarWidth: "thin", // Firefox
          "&::-webkit-scrollbar": {
            height: 6,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 3,
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex", // allows wrapping within scroll area
            gap: 1,
          }}
        >
          {tabs.map((label) => (
            <Box
              key={label}
              sx={{
                px: 2,
                py: 0.8,
                bgcolor: label === selectedTab ? "#28bea9" : "#f5f5f5",
                color: label === selectedTab ? "white" : "#6e6e6e",
                borderRadius: "16px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
                flexShrink: 0, // prevent shrinking on small screens
                "&:hover": {
                  bgcolor: label === selectedTab ? "#24a693" : "#eaeaea",
                },
              }}
              onClick={() => {
                if (loading || data.length == 0) return;
                setSelectedTab(label);
                setPage(1);
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>


      {/* 🔹 Requests header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {filteredRequests.length} Requests
        </Typography>

        <Box sx={{ position: "relative" }}>
          <IconButton
  size="small"
  onClick={() => setFilterOpen((prev) => !prev)}
  sx={{
    color: statusFilter !== "All" ? "white" : "#28bea9",
    bgcolor: statusFilter !== "All" ? "#28bea9" : "#f1fdfb",
    borderRadius: 1,
    "&:hover": {
      bgcolor: statusFilter !== "All" ? "#24a693" : "#e8f8f6",
    },
  }}
>
  <FilterListIcon fontSize="small" />
</IconButton>

          {/* 🔹 Inline Filter Box */}
          {filterOpen && (
            <Box
              ref={filterRef}
              sx={{
                position: "absolute",
                top: "115%",
                right: 0,
                bgcolor: "white",
                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                borderRadius: 2,
                p: 1.5,
                minWidth: 200,
                zIndex: 10,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                Filter by Status
              </Typography>
              <RadioGroup
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
              >
                {statusOptions.map((status) => (
                  <FormControlLabel
                    key={status}
                    value={status}
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: 13 }}>{status}</Typography>}
                  />
                ))}
              </RadioGroup>
            </Box>
          )}
        </Box>

        {/* <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
              sx={{
                color: "#28bea9",
                p: 0,
                "&.Mui-checked": { color: "#5fc3b1" },
                mr: 0.4,
              }}
            />
          }
          label="Select All"
          sx={{
            "& .MuiFormControlLabel-label": { fontSize: 14 },
            marginRight: 0,
          }}
        /> */}
      </Box>

      {/* 🔹 Requests list */}
      <Box sx={{ flex: 1, overflowY: "auto", my: 2, px: 2 }}>
        {loading ? (
          <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress
              size={24}
              sx={{ display: "block", mx: "auto", my: 2 }}
            />
          </Box>
        ) : paginatedRequests.length === 0 ? (
          <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Typography sx={{ textAlign: "center", color: "#9e9e9e" }}>
              No requests
            </Typography>
          </Box>
        ) : (
          paginatedRequests.map((req) => {
            const currentData = requestDataMap[req.id] ?? req.data;
            return (<RequestCard
              key={req.id}
              id={req.id}
              type={req.type}
              storeName={req.storeName}
              storeId={req.storeId}
              salesRepId={req.salesRepId}
              currentCreditLimit={req.currentCreditLimit}
              availCreditLimit={req.availCreditLimit}
              data={currentData}
              status={req.status}
              onDataChange={(newData) => handleDataChange(req.id, newData)}
              onApprove={(data, type, id, currentCreditLimit) => {
                setRequestDataMap(prev => {
                  const updated = { ...prev };
                  delete updated[id];  
                  return updated;
                });
                onApprove(data, type, id, currentCreditLimit);
              }}
              onReject={(data, type, id) => {
                setRequestDataMap(prev => {
                  const updated = { ...prev };
                  delete updated[id];  
                  return updated;
                });
                onReject(data, type, id);
              }}
              checked={selectedRequests.has(req.id)}
              onChange={(checked: boolean) => handleRequestSelect(req.id, checked)}
              labelMap={labelMap}
              outletType={req.outletType}
              invoiceNumber={req.invoiceNumber}
              orderDate={req.orderDate}
            />)
          })
        )}
      </Box>

      {/* 🔹 Footer (Pagination + Buttons) */}

      {totalPages > 1 && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            borderTop: "1px solid #eee",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
            bgcolor: "white",
            gap: 1,
            py: 1
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            size="small"
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default RequestList;

